import React, { useEffect, useState } from "react";
import { arrayMove, SortableContainer, SortableElement } from "react-sortable-hoc";
import { sortOrganizationIntegrationSettings } from "../../../../api-client/organization/OrganizationIntegrationSettingsAPIClient";
import { OrganizationIntegrationSettings } from "../../../../models/Entities";
import { showErrorToast } from "../../../shared/Toasts";
import IntegrationSettingsRow from "./IntegrationSettingsRow";

interface Props {
  category: string;
  organizationIntegrationsSettings: Array<OrganizationIntegrationSettings>;
  onIntegrationUpdate: any;
}

const SortableItem = SortableElement<{
  organizationIntegrationSettings: OrganizationIntegrationSettings;
  onIntegrationToggle: (data: OrganizationIntegrationSettings) => void;
}>(
  ({
    organizationIntegrationSettings,
    onIntegrationToggle,
  }: {
    organizationIntegrationSettings: OrganizationIntegrationSettings;
    onIntegrationToggle: (data: OrganizationIntegrationSettings) => void;
  }) => {
    return (
      <IntegrationSettingsRow
        key={organizationIntegrationSettings.integration.id}
        organizationIntegrationSettings={organizationIntegrationSettings}
        onIntegrationToggle={onIntegrationToggle}
      />
    );
  },
);

const SortableList = SortableContainer<{
  items: OrganizationIntegrationSettings[];
  onIntegrationToggle: (data: OrganizationIntegrationSettings) => void;
}>(
  ({
    items,
    onIntegrationToggle,
  }: {
    items: OrganizationIntegrationSettings[];
    onIntegrationToggle: (data: OrganizationIntegrationSettings) => void;
  }) => {
    return (
      <div className="flex flex-col gap-y-5">
        {items.map((value, index) => (
          <SortableItem
            key={`item-${value.id}`}
            index={index}
            organizationIntegrationSettings={value}
            onIntegrationToggle={onIntegrationToggle}
          />
        ))}
      </div>
    );
  },
);

const SortableIntegrationContainer = ({
  organizationIntegrationsSettings,
  onIntegrationUpdate,
}: Props) => {
  const [sortableOrganizationIntegrationsSettings, setSortableOrganizationIntegrationsSettings] =
    useState<Array<OrganizationIntegrationSettings>>(organizationIntegrationsSettings);

  useEffect(() => {
    setSortableOrganizationIntegrationsSettings(organizationIntegrationsSettings);
  }, [organizationIntegrationsSettings]);

  const onIntegrationToggle = (updatedOrgIntegration: OrganizationIntegrationSettings) => {
    onIntegrationUpdate(updatedOrgIntegration, sortableOrganizationIntegrationsSettings);
  };

  const onSortEnd = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
    if (sortableOrganizationIntegrationsSettings) {
      const revertState = Object.assign([], sortableOrganizationIntegrationsSettings);
      const orgIntegrationId = sortableOrganizationIntegrationsSettings[oldIndex].id;
      setSortableOrganizationIntegrationsSettings(
        arrayMove(sortableOrganizationIntegrationsSettings, oldIndex, newIndex),
      );
      sortOrganizationIntegrationSettings({
        settingsId: orgIntegrationId,
        displayOrder: newIndex,
        onSort: () => {},
        onSortError: () => {
          const errorName = sortableOrganizationIntegrationsSettings[newIndex].integration.name;
          setSortableOrganizationIntegrationsSettings(revertState);
          showErrorToast(`Whoops! Something went wrong sorting ${errorName}. Please try again`);
        },
      });
    }
  };

  return (
    <>
      {sortableOrganizationIntegrationsSettings && (
        <SortableList
          items={sortableOrganizationIntegrationsSettings}
          onIntegrationToggle={onIntegrationToggle}
          onSortEnd={onSortEnd}
          lockAxis="y"
          useDragHandle
          helperClass="active-sorted-row"
          lockToContainerEdges
        />
      )}
    </>
  );
};

export default SortableIntegrationContainer;
